import {
  createCanvasSceneRenderer,
  type CanvasRendererContextBoundary,
} from "./canvas-renderer.js";
import { createDomSceneRenderer, type DomRendererOptions } from "./dom-renderer.js";
import type { RendererHostFactories } from "./renderer-host.js";
import type { RendererFailure } from "./renderer-contract.js";
import {
  createThreeSceneRenderer,
  type ThreeRendererConstructionResources,
} from "./three-renderer.js";

export interface StageRendererDependencies {
  readonly three?: ThreeRendererConstructionResources;
  readonly canvas?: CanvasRendererContextBoundary;
  readonly dom?: DomRendererOptions;
}

export interface StageRendererFactoryConstructionBoundary {
  readonly afterThreeRendererCreated?: (
    root: HTMLElement,
    renderer: ReturnType<typeof createThreeSceneRenderer>,
  ) => void;
}

export interface StageRendererFactoryConstructionOptions {
  readonly constructionBoundary?: StageRendererFactoryConstructionBoundary;
}

function initializationFailure(recoverable: boolean, cause: unknown): Error & RendererFailure {
  return Object.assign(new Error(
    recoverable
      ? "Direct renderer late initialization failure"
      : "Direct renderer cleanup failed after late initialization failure",
    { cause },
  ), {
    kind: "three" as const,
    phase: "initialize" as const,
    code: recoverable ? "initialization" as const : "resource" as const,
    recoverable,
  }) as unknown as Error & RendererFailure;
}

function createStageThreeRenderer(
  root: HTMLElement,
  dependencies: StageRendererDependencies,
  options: StageRendererFactoryConstructionOptions,
  scenePlan: Parameters<RendererHostFactories["three"]>[1],
) {
  const renderer = createThreeSceneRenderer(root, dependencies.three ?? {}, scenePlan);
  try {
    options.constructionBoundary?.afterThreeRendererCreated?.(root, renderer);
    return renderer;
  } catch (initializationCause) {
    let cleanupCause: unknown;
    let verificationCause: unknown;
    try {
      renderer.destroy();
    } catch (cause) {
      cleanupCause = cause;
    }

    try {
      const unsubscribe = renderer.onFailure(() => undefined);
      unsubscribe();
    } catch (cause) {
      cleanupCause ??= cause;
      verificationCause ??= cause;
    }

    try {
      for (const canvas of root.querySelectorAll('[data-testid="experience-canvas"]')) canvas.remove();
    } catch (cause) {
      cleanupCause ??= cause;
      verificationCause ??= cause;
    }

    let diagnostics: ReturnType<typeof renderer.diagnostics> | null = null;
    try {
      diagnostics = renderer.diagnostics();
    } catch (cause) {
      verificationCause ??= cause;
    }

    let remainingCanvasCount: number | null = null;
    try {
      remainingCanvasCount = root.querySelectorAll('[data-testid="experience-canvas"]').length;
    } catch (cause) {
      verificationCause ??= cause;
    }

    if (verificationCause !== undefined) {
      throw initializationFailure(false, verificationCause);
    }
    const cleaned = diagnostics !== null
      && diagnostics.cleanupComplete === true
      && diagnostics.canvasCount === 0
      && diagnostics.objectCount === 0
      && diagnostics.activeSubscriptions === 0
      && remainingCanvasCount === 0;
    if (!cleaned) {
      throw initializationFailure(false, cleanupCause ?? new Error("Direct renderer cleanup remained incomplete"));
    }
    throw initializationFailure(true, initializationCause);
  }
}

export function createStageRendererFactories(
  dependencies: StageRendererDependencies = {},
  options: StageRendererFactoryConstructionOptions = {},
): RendererHostFactories {
  const factories: RendererHostFactories = {
    three: (root, scenePlan) => createStageThreeRenderer(root, dependencies, options, scenePlan),
    canvas: (root) => createCanvasSceneRenderer(root, dependencies.canvas),
    dom: (root) => createDomSceneRenderer(root, dependencies.dom),
  };
  return Object.freeze(factories);
}
